/**
 * What each scan produced in SQL Accounting.
 *
 * One row per scan, because that is the question people actually ask — "the lorry came in on
 * Tuesday, what did it create?" — and because a supplier inbound produces TWO documents from one
 * scan: the purchase order it raised and the receipt chained to it.
 *
 * Scans that produced NOTHING are on the same list. A blocked scan means goods are physically in
 * the warehouse with no paperwork, which is more urgent than any of the successful rows, so it is
 * not filed away on a separate screen.
 *
 * Unit prices are the supplier's cost. The API blanks them without `bi.cost.read`, and this shows
 * the column as withheld rather than as zero.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, money, qty } from '../lib/api';
import { businessDate, dateTime, MYT, preciseTitle } from '../lib/time';
import { Card, Table, Loading, Empty, cell, when } from '../components/ui';

type Posting = {
  order_no: string;
  po_docno: string | null;
  gr_docno: string | null;
  do_docno: string | null;
  /** The Stock Transfer's docno — the internal-transfer flow's only document (resource `/stocktransfer`). */
  transfer_docno: string | null;
  source_docno: string | null;
  direction: 'IN' | 'OUT' | 'TRANSFER' | null;
  status: string | null;
  lines_posted: number | null;
  qty: string | null;
  posted_at: string | null;
  order_type: string | null;
  source_code: string | null;
  target_code: string | null;
  submit_time: string | null;
  scanned_qty: string | null;
  decision_id: number | null;
  decision_kind: string | null;
  decision_status: string | null;
  decision_summary: string | null;
};

type Line = {
  itemcode: string | null;
  uom: string | null;
  location: string | null;
  qty: string | null;
  unitprice: string | null;
  fromdoctype: string | null;
  fromdockey: number | null;
  fromdtlkey: number | null;
};

type Document = {
  id: number;
  resource: string;
  docno: string | null;
  status: string;
  direction: string;
  source_docno: string | null;
  created_at: string | null;
  error: string | null;
  supplier: string | null;
  docdate: string | null;
  terms: string | null;
  docref4: string | null;
  lines: Line[];
};

const RESOURCE_LABEL: Record<string, string> = {
  '/purchaseorder': 'Purchase order',
  '/goodsreceived': 'Goods received',
  '/deliveryorder': 'Delivery order',
  '/stocktransfer': 'Stock transfer',
};

/** Plain word for `direction`, never the raw code — same three values as `posting.direction`. */
const DIRECTION_LABEL: Record<'IN' | 'OUT' | 'TRANSFER', string> = {
  IN: 'Inbound',
  OUT: 'Outbound',
  TRANSFER: 'Internal transfer',
};

/** A supplier inbound is the only scan that RAISES a commitment, so it is labelled as one. */
function origin(p: Posting): { label: string; cls: string; title: string } {
  if (p.order_type === '222')
    return {
      label: 'supplier scan',
      cls: 'bg-sky-light/40 text-brand-800',
      title: 'Scanned on the handheld with no purchase order — the scan raised one',
    };
  if (p.direction === 'TRANSFER')
    return {
      label: DIRECTION_LABEL.TRANSFER,
      cls: 'bg-amber-50 text-amber-700',
      title: 'Moved between two of LSK’s own locations — no purchase order or sales order involved',
    };
  if (p.direction === 'OUT')
    return {
      label: 'from SO',
      cls: 'bg-slate-100 text-slate-600',
      title: 'Picked against a sales order',
    };
  return {
    label: 'from PO',
    cls: 'bg-slate-100 text-slate-600',
    title: 'Received against a purchase order',
  };
}

export default function RfidPostings() {
  const [kind, setKind] = useState<'ALL' | 'PO' | 'GR' | 'DO' | 'TRANSFER'>('ALL');
  const [open, setOpen] = useState<string | null>(null);
  const queryClient = useQueryClient();

  /**
   * Resolve one blocked transfer.
   *
   * `retry` does NOT post — it closes the decision and re-offers the event to `scan:poll`, which
   * re-runs the same guard and the same idempotency checks. So the list will not change the
   * instant this returns; the row clears on a later poll if the cause really was fixed. Refetching
   * is still right: the decision itself closes immediately either way.
   */
  const resolve = useMutation({
    mutationFn: ({
      id,
      action,
      reason,
    }: {
      id: number;
      action: 'retry' | 'dismiss';
      reason?: string;
    }) =>
      api.post(`/api/v1/decisions/${id}/${action}`, action === 'dismiss' ? { reason } : undefined),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['rfid-postings'] }),
  });

  const list = useQuery({
    queryKey: ['rfid-postings', kind],
    queryFn: () =>
      api.get<{ postings: Posting[] }>(
        `/api/v1/rfid/postings${kind === 'ALL' ? '' : `?kind=${kind}`}`,
      ),
    refetchInterval: 60_000,
  });

  const detail = useQuery({
    queryKey: ['rfid-posting', open],
    enabled: open !== null,
    queryFn: () =>
      api.get<{ documents: Document[]; costVisible: boolean }>(
        `/api/v1/rfid/postings/${encodeURIComponent(open ?? '')}`,
      ),
  });

  const rows = list.data?.postings ?? [];

  return (
    <div className="space-y-6">
      <Card
        title="Documents raised from scans"
        subtitle="What each RFID scan created in SQL Accounting. A supplier inbound raises its own purchase order and the receipt chained to it."
        actions={
          <div className="flex gap-1">
            {(
              [
                ['ALL', 'All'],
                ['PO', 'Purchase orders'],
                ['GR', 'Receipts'],
                ['DO', 'Deliveries'],
                ['TRANSFER', 'Transfers'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setKind(k)}
                className={`rounded px-2.5 py-1 text-xs ${
                  kind === k
                    ? 'bg-brand-600 text-white'
                    : 'border border-slate-300 hover:bg-brand-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        }
      >
        {list.isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>
            Nothing posted yet. While DRY_RUN is on, payloads are logged and simulated rather than
            sent, so this stays empty by design.
          </Empty>
        ) : (
          <Table
            head={[
              'Scan',
              'Origin',
              'From → To',
              'Scanned',
              'Purchase order',
              'Receipt / delivery',
              'Lines',
              'Posted qty',
              `When (${MYT})`,
              'State',
              '',
            ]}
            rows={rows.map((p) => {
              const o = origin(p);
              const blocked = p.decision_status === 'open';
              return [
                <span className="font-mono text-xs">{p.order_no}</span>,
                <span className={`rounded px-2 py-0.5 text-xs ${o.cls}`} title={o.title}>
                  {o.label}
                </span>,
                `${p.source_code ?? '—'} → ${p.target_code ?? '—'}`,
                cell(Number(p.scanned_qty ?? 0), qty(p.scanned_qty)),
                p.po_docno ? (
                  <span className="font-medium text-brand-700">{p.po_docno}</span>
                ) : (
                  <span className="text-slate-400">
                    {/* `source_docno` is a real SQL Accounting docno for IN/OUT, but for TRANSFER
                        it is `pair.outboundOrderNo` — a Smartlink order number, not a document
                        that exists under this "Purchase order" header. Showing it there sends an
                        operator looking for a purchase order that was never raised. */}
                    {p.direction === 'TRANSFER' ? '—' : (p.source_docno ?? '—')}
                  </span>
                ),
                (p.gr_docno ?? p.do_docno ?? p.transfer_docno) ? (
                  <span className="text-emerald-700">
                    {p.gr_docno ?? p.do_docno ?? p.transfer_docno}
                  </span>
                ) : (
                  <span className="text-slate-400">—</span>
                ),
                cell(Number(p.lines_posted ?? 0), p.lines_posted ? String(p.lines_posted) : '—'),
                cell(Number(p.qty ?? 0), p.qty ? qty(p.qty) : '—'),
                when(p.posted_at ?? p.submit_time),
                blocked ? (
                  <span
                    className="rounded bg-alert/20 px-2 py-0.5 text-xs text-alert"
                    title={p.decision_summary ?? ''}
                  >
                    {p.decision_kind}
                  </span>
                ) : p.status === 'posted' ? (
                  <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                    posted
                  </span>
                ) : (
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                    {p.status ?? p.decision_kind ?? '—'}
                  </span>
                ),
                (p.po_docno ?? p.gr_docno ?? p.do_docno ?? p.transfer_docno) ? (
                  <button
                    onClick={() => setOpen(open === p.order_no ? null : p.order_no)}
                    className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50"
                  >
                    {open === p.order_no ? 'Hide' : 'Payload'}
                  </button>
                ) : /* An OPEN decision on a TRANSFER is the only thing that can be resolved here.
                       Retry re-offers the event to `scan:poll` — it never posts directly. Dismiss
                       closes a transfer that can never post (LSK's existing ones all target the
                       tenant root, which is baked into the Smartlink order), so the queue can
                       reach empty. Inbound and outbound have no replay path, so they get no
                       buttons rather than a promise the system cannot keep. */
                blocked && p.direction === 'TRANSFER' && p.decision_id !== null ? (
                  <span className="flex gap-1">
                    <button
                      onClick={() => resolve.mutate({ id: p.decision_id!, action: 'retry' })}
                      disabled={resolve.isPending}
                      className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50 disabled:opacity-50"
                    >
                      Retry
                    </button>
                    <button
                      onClick={() => {
                        const reason = window.prompt(
                          'Why is this transfer being dismissed? This is the only record of why nothing was posted.',
                        );
                        if (reason && reason.trim())
                          resolve.mutate({ id: p.decision_id!, action: 'dismiss', reason });
                      }}
                      disabled={resolve.isPending}
                      className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-alert/10 disabled:opacity-50"
                    >
                      Dismiss
                    </button>
                  </span>
                ) : (
                  <span className="text-slate-300">—</span>
                ),
              ];
            })}
          />
        )}
      </Card>

      {open && (
        <Card
          title={`Exactly what was sent — ${open}`}
          subtitle="Stored at post time, not rebuilt. A document questioned later is answered with what we actually sent."
        >
          {detail.isLoading ? (
            <Loading />
          ) : (
            (detail.data?.documents ?? []).map((d) => (
              <div key={d.id} className="mb-6 last:mb-0">
                <div className="mb-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
                  <span className="font-semibold text-brand-800">
                    {RESOURCE_LABEL[d.resource] ?? d.resource}
                  </span>
                  <span className="font-mono text-xs">{d.docno ?? '(no number)'}</span>
                  {d.supplier && <span className="text-slate-500">supplier {d.supplier}</span>}
                  {/* The document's business date — a calendar day SQL Accounting holds with no
                      time and no zone. Shown as stored; converting it would move the document. */}
                  {d.docdate && (
                    <span className="text-slate-500">dated {businessDate(d.docdate)}</span>
                  )}
                  <span className="text-slate-500" title={preciseTitle(d.created_at)}>
                    sent {dateTime(d.created_at)}
                  </span>
                  {d.terms && <span className="text-slate-500">terms {d.terms}</span>}
                  {d.docref4 && (
                    <span
                      className="text-slate-500"
                      title="Where the scan's order number is stamped"
                    >
                      docref4 {d.docref4}
                    </span>
                  )}
                  {d.error && <span className="text-alert">{d.error}</span>}
                </div>
                <Table
                  head={['Item', 'UOM', 'Location', 'Qty', 'Unit price', 'Chained to']}
                  rows={d.lines.map((l) => [
                    <span className="font-mono text-xs">{l.itemcode ?? '—'}</span>,
                    l.uom ?? '—',
                    l.location ?? '—',
                    cell(Number(l.qty ?? 0), qty(l.qty)),
                    l.unitprice !== null ? (
                      money(l.unitprice)
                    ) : detail.data?.costVisible ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <span className="text-slate-400" title="Requires the cost permission">
                        withheld
                      </span>
                    ),
                    l.fromdtlkey ? (
                      <span className="font-mono text-xs text-slate-500">
                        {l.fromdoctype} {l.fromdockey}/{l.fromdtlkey}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    ),
                  ])}
                />
              </div>
            ))
          )}
        </Card>
      )}
    </div>
  );
}
